/**
 * CDA icon set — inline SVG, Lucide-style (24x24 grid, 1.5–2px stroke, round
 * caps, outline only). Follows the established shared-ui pattern of hand-drawn
 * inline SVG (see ActionAssuranceBadge, ToolIntegrityPill) so the kit adds no
 * runtime dependency and renders identically in the host build and vitest.
 *
 * Zero emoji — replaces the emoji glyphs used in plugin-local CDA views.
 */

const React = (globalThis as any).React;

export type CdaIconName =
  | 'shield'
  | 'shield-check'
  | 'shield-alert'
  | 'lock'
  | 'key'
  | 'alert-triangle'
  | 'check'
  | 'chevron-right'
  | 'chevron-down'
  | 'eye'
  | 'eye-off'
  | 'copy'
  | 'info'
  | 'file-check'
  | 'circle'
  | 'circle-dot'
  | 'minus'
  | 'clock'
  | 'wand'
  | 'arrow-right'
  | 'external-link';

// Each entry is a list of [tag, attrs] child primitives drawn on the 24x24 grid.
type Prim = [string, Record<string, string | number>];

const PATHS: Record<CdaIconName, Prim[]> = {
  shield: [['path', { d: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z' }]],
  'shield-check': [
    ['path', { d: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z' }],
    ['path', { d: 'm9 12 2 2 4-4' }],
  ],
  'shield-alert': [
    ['path', { d: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z' }],
    ['path', { d: 'M12 8v4' }],
    ['path', { d: 'M12 16h.01' }],
  ],
  lock: [
    ['rect', { x: 3, y: 11, width: 18, height: 11, rx: 2 }],
    ['path', { d: 'M7 11V7a5 5 0 0 1 10 0v4' }],
  ],
  key: [
    ['circle', { cx: 7.5, cy: 15.5, r: 5.5 }],
    ['path', { d: 'm21 2-9.6 9.6' }],
    ['path', { d: 'm15.5 7.5 3 3L22 7l-3-3' }],
  ],
  'alert-triangle': [
    ['path', { d: 'm21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3z' }],
    ['path', { d: 'M12 9v4' }],
    ['path', { d: 'M12 17h.01' }],
  ],
  check: [['path', { d: 'M20 6 9 17l-5-5' }]],
  'chevron-right': [['path', { d: 'm9 18 6-6-6-6' }]],
  'chevron-down': [['path', { d: 'm6 9 6 6 6-6' }]],
  eye: [
    ['path', { d: 'M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z' }],
    ['circle', { cx: 12, cy: 12, r: 3 }],
  ],
  'eye-off': [
    ['path', { d: 'M9.88 9.88a3 3 0 1 0 4.24 4.24' }],
    ['path', { d: 'M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68' }],
    ['path', { d: 'M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61' }],
    ['path', { d: 'm2 2 20 20' }],
  ],
  copy: [
    ['rect', { x: 9, y: 9, width: 13, height: 13, rx: 2 }],
    ['path', { d: 'M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1' }],
  ],
  info: [
    ['circle', { cx: 12, cy: 12, r: 10 }],
    ['path', { d: 'M12 16v-4' }],
    ['path', { d: 'M12 8h.01' }],
  ],
  'file-check': [
    ['path', { d: 'M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5z' }],
    ['path', { d: 'M14 2v6h6' }],
    ['path', { d: 'm9 15 2 2 4-4' }],
  ],
  circle: [['circle', { cx: 12, cy: 12, r: 10 }]],
  'circle-dot': [
    ['circle', { cx: 12, cy: 12, r: 10 }],
    ['circle', { cx: 12, cy: 12, r: 3, fill: 'currentColor' }],
  ],
  minus: [['path', { d: 'M5 12h14' }]],
  clock: [
    ['circle', { cx: 12, cy: 12, r: 10 }],
    ['path', { d: 'M12 6v6l4 2' }],
  ],
  wand: [
    ['path', { d: 'M15 4V2' }],
    ['path', { d: 'M15 16v-2' }],
    ['path', { d: 'M8 9h2' }],
    ['path', { d: 'M20 9h2' }],
    ['path', { d: 'M17.8 11.8 19 13' }],
    ['path', { d: 'M15 9h.01' }],
    ['path', { d: 'M17.8 6.2 19 5' }],
    ['path', { d: 'm3 21 9-9' }],
    ['path', { d: 'M12.2 6.2 11 5' }],
  ],
  'arrow-right': [
    ['path', { d: 'M5 12h14' }],
    ['path', { d: 'm12 5 7 7-7 7' }],
  ],
  'external-link': [
    ['path', { d: 'M15 3h6v6' }],
    ['path', { d: 'M10 14 21 3' }],
    ['path', { d: 'M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6' }],
  ],
};

export interface CdaIconProps {
  name: CdaIconName;
  size?: number;
  color?: string;
  strokeWidth?: number;
  className?: string;
  title?: string;
}

export function CdaIcon({ name, size = 16, color = 'currentColor', strokeWidth = 2, className, title }: CdaIconProps) {
  const prims = PATHS[name] || [];
  return React.createElement(
    'svg',
    {
      width: size,
      height: size,
      viewBox: '0 0 24 24',
      fill: 'none',
      stroke: color,
      strokeWidth,
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
      className: className || undefined,
      'aria-hidden': title ? undefined : 'true',
      role: title ? 'img' : undefined,
      'aria-label': title || undefined,
      'data-cda-icon': name,
    },
    title ? React.createElement('title', null, title) : null,
    ...prims.map(([tag, attrs], i) =>
      React.createElement(tag, { key: i, ...attrs }),
    ),
  );
}
